import {
  MultiSelectorBadgeItem,
  MultiSelectorSeparator,
  MultiSelectorContent,
  MultiSelectorTrigger,
  MultiSelectorInput,
  MultiSelectorList,
  MultiSelector,
  Loading,
  Button,
} from '@ui/components';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import React, { ComponentProps, useState, useMemo } from 'react';
import { LabelType, LabelDto } from '@interfaces/models';
import { saveLabel } from '@api/labels/labels.service';
import { useGetLabels } from '@shared/hooks';
import { Plus } from '@ui/components/icons';

export interface LabelsPickerProps {
  componentProps?: Partial<ComponentProps<typeof MultiSelector>> & { hasShadow?: boolean };
  size?: 'medium' | 'small' | 'large';
  searchPlaceholder?: string;
  children: React.ReactNode;
  saveButtonLabel?: string;
  labelType: LabelType;
  isLoading?: boolean;
  required?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  floating?: boolean;
  values?: string[];
  asChild?: boolean;
  isOpen?: boolean;
  onValuesChange?: (value: string[]) => void;
  setIsOpen?: (value: boolean) => void;
  onSave?: (value: string[]) => void;
}

export const LabelsPicker = ({
  searchPlaceholder,
  saveButtonLabel,
  componentProps,
  isLoading,
  labelType,
  required,
  disabled,
  floating,
  children,
  hasError,
  asChild,
  isOpen,
  values,
  size,
  onValuesChange,
  setIsOpen,
  onSave,
}: LabelsPickerProps) => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);

  const [searchValue, setSearchValue] = useState<string>('');

  const { labels, isFetching } = useGetLabels(labelType);

  const { mutate: addLabel, isPending } = useMutation({
    mutationFn: (name: string) => saveLabel(labelType, name),
    onMutate: () => setSearchValue(''),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['labels', labelType] }).then(() => {
        setSelected([...selected, data.id]);
        onValuesChange?.([...(values || []), data.id]);
      });
    },
  });

  const options = labels.map((label: LabelDto) => ({
    label: label.name,
    value: label.id,
  }));

  const searchValueExists = useMemo(() => {
    return options.some((el) => el.label.trim() === searchValue.trim());
  }, [searchValue, options]);

  const { hasShadow, ...multiSelectorProps } = componentProps || {};

  return (
    <>
      <MultiSelector
        onValuesChange={onValuesChange ?? setSelected}
        isLoading={isFetching || isPending}
        values={values || selected}
        setIsOpen={setIsOpen}
        required={required}
        hasError={hasError}
        disabled={disabled}
        floating={floating}
        className="w-auto"
        isOpen={isOpen}
        {...multiSelectorProps}
      >
        <MultiSelectorTrigger hasShadow={!!hasShadow} asChild={asChild} size={size}>
          {children}
        </MultiSelectorTrigger>
        <MultiSelectorContent className="min-w-72">
          <MultiSelectorInput
            placeholder={searchPlaceholder}
            onValueChange={setSearchValue}
            value={searchValue}
            maxLength={100}
          />
          <MultiSelectorSeparator />
          <Loading isLoading={isFetching}>
            <MultiSelectorList displayEmptyContent={false} variant="wrapped">
              {options.map((option) => (
                <MultiSelectorBadgeItem
                  value={option.value}
                  label={option.label}
                  key={option.value}
                />
              ))}

              {searchValue && !searchValueExists && (
                <MultiSelectorBadgeItem
                  label={
                    <>
                      {searchValue}
                      <Plus className="!size-3" />
                    </>
                  }
                  onSelect={() => addLabel(searchValue.trim())}
                  value={searchValue}
                  forceMount
                />
              )}
            </MultiSelectorList>
          </Loading>

          {onSave && (
            <>
              <MultiSelectorSeparator />

              <div className="p-2">
                <Button
                  onClick={() => onSave(selected)}
                  isLoading={isLoading}
                  disabled={isLoading}
                  variant="primary"
                  type="button"
                  wide
                >
                  {saveButtonLabel}
                </Button>
              </div>
            </>
          )}
        </MultiSelectorContent>
      </MultiSelector>
    </>
  );
};
